import { ClientTypes } from "../../auth/types/authType";
import { Company } from "../../setups/types/setupTypes";
import {
  CountriesList,
  Employee,
  GulfCountries,
} from "../types/processTypes";
import type { OfficePartnerContext } from "../../calling/types/officePartnerContext";

const DEFAULT_APPLIED_COUNTRY = CountriesList.saudi_arabia;

/** Picks the LocalAgency partner linked to a foreign-agent office. */
export const localAgencyOf = (office?: Company | null): Company | undefined =>
  office?.partners?.find((p) => p.type === ClientTypes.LocalAgency);

export type OfficePartnerSource = {
  foreignOffices?: Company[];
  parentCompany?: Company;
  user?: { company?: Company };
};

export function officePartnerSourceFromContext(
  context?: OfficePartnerContext | null,
  user?: { company?: Company },
): OfficePartnerSource {
  return {
    foreignOffices: context?.foreignOffices,
    parentCompany: context?.parentCompany,
    user,
  };
}

export function foreignAgentPartnersFromSource(
  source?: OfficePartnerSource,
): Company[] {
  if (source?.foreignOffices?.length) {
    return source.foreignOffices;
  }
  return foreignAgentPartnersFromUser(source?.user);
}

export function foreignAgentPartnersFromUser(user?: {
  company?: Company;
}): Company[] {
  const pools = [
    ...(user?.company?.partners ?? []),
    ...(user?.company?.parentCompany?.partners ?? []),
  ];
  const seen = new Set<string>();
  return pools.filter((p) => {
    if (p.type !== ClientTypes.ForeignAgent || !p._id) return false;
    const id = String(p._id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Full foreign-agent row (includes address + nested LocalAgency partners). */
export function resolveOfficeFromUserPartners(
  office: Company | undefined | null,
  source?: OfficePartnerSource,
): Company | undefined {
  if (!office?._id) return undefined;
  const id = String(office._id);
  return (
    foreignAgentPartnersFromSource(source).find((p) => String(p._id) === id) ??
    (office.type === ClientTypes.ForeignAgent ? office : undefined)
  );
}

export function countryFromOffice(
  office?: Company | null,
): CountriesList {
  const country = office?.address?.country as CountriesList | undefined;
  if (
    country &&
    Object.values(GulfCountries).includes(country as GulfCountries)
  ) {
    return country;
  }
  return DEFAULT_APPLIED_COUNTRY;
}

/** Resolves the employee's foreign-agent office using partner lists. */
export function resolveForeignOfficePartner(
  emp: Employee | null | undefined,
  source?: OfficePartnerSource,
): Company | undefined {
  const partnerId = emp?.partner?._id;
  if (!partnerId) return undefined;

  const id = String(partnerId);
  const fromList = foreignAgentPartnersFromSource(source).find(
    (p) => String(p._id) === id,
  );
  if (fromList) return fromList;

  if (emp?.partner?.type === ClientTypes.ForeignAgent) {
    return emp.partner;
  }

  return undefined;
}

/** Short office label for tables (matches Employees list truncation). */
export function officeDisplayLabel(
  office?: Company | null,
  direction: "ltr" | "rtl" = "ltr",
): string | undefined {
  if (!office) return undefined;
  if (direction === "rtl" && office.nameAmharic?.trim()) {
    return office.nameAmharic.trim();
  }
  const name = office.displayName?.trim();
  if (!name) return office.nameAmharic?.trim();
  const space = name.indexOf(" ");
  return space > 0 && name.length > 10 ? name.substring(0, space) : name;
}

/** Foreign-agent office for an employee row. */
export function employeeOfficeForDisplay(
  emp: Employee | null | undefined,
  source?: OfficePartnerSource,
): Company | undefined {
  return (
    resolveForeignOfficePartner(emp, source) ??
    resolveOfficeFromUserPartners(emp?.partner, source) ??
    emp?.partner ??
    undefined
  );
}

/** LocalAgency owner for an employee row. */
export function employeeOwnerForDisplay(
  emp: Employee | null | undefined,
  source?: OfficePartnerSource,
): Company | undefined {
  const ownerId = emp?.owner?._id ? String(emp.owner._id) : undefined;
  if (ownerId) {
    if (
      source?.user?.company?.type === ClientTypes.LocalAgency &&
      String(source.user.company._id) === ownerId
    ) {
      return source.user.company;
    }
    for (const office of foreignAgentPartnersFromSource(source)) {
      const agency = localAgencyOf(office);
      if (agency?._id && String(agency._id) === ownerId) {
        return agency;
      }
    }
    if (emp?.owner?.type === ClientTypes.LocalAgency) {
      return emp.owner;
    }
  }

  return (
    resolveOwnerForForeignOffice(
      employeeOfficeForDisplay(emp, source),
      source,
    ) ?? emp?.owner
  );
}

/** Owner for a contracted employee: LocalAgency under the selected foreign office. */
export function resolveOwnerForForeignOffice(
  office: Company | undefined,
  source?: OfficePartnerSource,
): Company | undefined {
  const resolvedOffice = resolveOfficeFromUserPartners(office, source) ?? office;
  const fromOffice = localAgencyOf(resolvedOffice);
  if (fromOffice) return fromOffice;
  if (source?.user?.company?.type === ClientTypes.LocalAgency) {
    return source.user.company;
  }
  return undefined;
}

/** Parent company for owner scope (Moderator group). */
export function resolveParentCompanyForOffice(
  owner: Company | undefined,
  source?: OfficePartnerSource,
): Company | undefined {
  const ownerParent = owner?.parentCompany;
  if (ownerParent?._id) return ownerParent;
  if (source?.parentCompany?._id) return source.parentCompany;
  if (source?.user?.company?.type === ClientTypes.Moderator) {
    return source.user.company;
  }
  return source?.user?.company?.parentCompany;
}
